/**
 * «Дополнительные отчёты» — каталог обычных отчётов реестра.
 *
 * В главном меню их больше нет: там разделы (финансы, продажи, склад),
 * а таблицы с ручной группировкой — здесь, по категориям и с поиском.
 * Что попадает в каталог, решает доступ к отчёту в матрице: отдельной
 * настройки «показывать в каталоге» нет и быть не должно.
 */

import { useEffect, useMemo, useState } from 'react'
import { api } from '../api'
import { Link } from 'react-router-dom'
import type { CurrentUser } from '../types'
import { Badge, Card, Empty } from '../components/ui'
import { pluralize } from '../format'

const CATEGORY_ORDER = ['Финансы', 'Продажи', 'Склад', 'Персонал', 'Ручные данные']

/**
 * Из каких потоков выгрузки строится отчёт. Нет ни одного пакета потока —
 * отчёт пуст, и вести в него человека незачем: карточка говорит об этом сразу.
 */
const REPORT_STREAMS: Record<string, string[]> = {
  margin_by_department: ['sales'],
  sales_by_group: ['sales'],
  abc_customers: ['sales'],
  pnl_by_department: ['gl_turnover'],
  cashflow: ['cashflow'],
  receivables: ['settlements'],
  stock_turnover: ['stock_balance'],
  stock_current: ['stock_balance'],
  payroll: ['payroll'],
  pnl_management: ['sales', 'purchases'],
  payroll_services: ['purchases'],
  gross_profit_manual: ['excel_gross_profit'],
}

export function ReportsCatalog({ user }: { user: CurrentUser }) {
  const [query, setQuery] = useState('')
  // Потоки, по которым пришёл хоть один пакет. null — неизвестно (тогда все карточки — ссылки).
  const [loaded, setLoaded] = useState<Set<string> | null>(null)
  useEffect(() => {
    api
      .dashboard()
      .then((data) => setLoaded(new Set([...data.freshness.map((item) => item.stream), ...data.staging.filter((item) => item.rows > 0).map((item) => item.stream)])))
      .catch(() => setLoaded(null))
  }, [])
  const hasData = (code: string) => !loaded || !REPORT_STREAMS[code] || REPORT_STREAMS[code]!.some((stream) => loaded.has(stream))

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matched = user.reports.filter(
      (report) =>
        !needle ||
        report.title.toLowerCase().includes(needle) ||
        report.description.toLowerCase().includes(needle) ||
        report.category.toLowerCase().includes(needle),
    )
    const byCategory = new Map<string, CurrentUser['reports']>()
    for (const report of matched) byCategory.set(report.category, [...(byCategory.get(report.category) ?? []), report])
    return [...byCategory.entries()].sort(
      ([a], [b]) => (CATEGORY_ORDER.indexOf(a) + 1 || 99) - (CATEGORY_ORDER.indexOf(b) + 1 || 99),
    )
  }, [user.reports, query])

  if (user.reports.length === 0) {
    return (
      <Card>
        <Empty
          icon="reports"
          title="Отчёты вам пока не назначены"
          hint={
            user.support_contact
              ? `Чтобы получить доступ, напишите администратору: ${user.support_contact}.`
              : 'Чтобы получить доступ, обратитесь к администратору системы.'
          }
        />
      </Card>
    )
  }

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="row" style={{ alignItems: 'center' }}>
        <input
          className="input catalog-search"
          type="search"
          placeholder="Найти отчёт: «остатки», «ДДС», «ФОТ»…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Поиск по отчётам"
        />
        <span className="small muted">{pluralize(user.reports.length, 'отчёт', 'отчёта', 'отчётов')} доступно</span>
      </div>

      {groups.length === 0 ? (
        <Card>
          <Empty icon="search" title="Ничего не нашлось" hint="Попробуйте другое слово или очистите поиск." />
        </Card>
      ) : (
        groups.map(([category, reports]) => (
          <section key={category}>
            <h2 className="catalog-group__title">{category}</h2>
            <div className="catalog-grid">
              {reports.map((report) => {
                const { text, noSource } = splitDescription(report.description)
                if (!hasData(report.code)) {
                  return (
                    <div key={report.code} className="catalog-card catalog-card--off" title={report.description}>
                      <span className="catalog-card__head">
                        <span className="catalog-card__title">{report.title}</span>
                        <Badge>{noSource ? 'нет источника' : 'пока нет данных'}</Badge>
                      </span>
                      <span className="catalog-card__text">{text}</span>
                      <span className="catalog-card__note">
                        {noSource
                          ? 'Источник этого отчёта ещё не подключён — открывать нечего. Что уже можно посмотреть вместо него, сказано в описании.'
                          : 'Из 1С ещё не пришло ни одного пакета для этого отчёта — открывать нечего. Как только данные придут, карточка станет ссылкой.'}
                      </span>
                    </div>
                  )
                }
                return (
                  <Link
                    key={report.code}
                    to={`/reports/${report.code}`}
                    className={`catalog-card${report.unavailable_reason ? ' catalog-card--off' : ''}`}
                    title={report.description}
                  >
                    <span className="catalog-card__head">
                      <span className="catalog-card__title">{report.title}</span>
                      {noSource && <Badge>нет источника</Badge>}
                    </span>
                    <span className="catalog-card__text">{text}</span>
                    {report.unavailable_reason && (
                      <span className="catalog-card__note">
                        <Badge tone="warn">не откроется</Badge> {report.unavailable_reason}
                      </span>
                    )}
                  </Link>
                )
              })}
            </div>
          </section>
        ))
      )}
    </div>
  )
}

/**
 * Описание из реестра начинается с «ИСТОЧНИКА ПОКА НЕТ:», если данных
 * для отчёта в базе клиента нет. Это статус, а не текст: бейдж, а не капс.
 */
function splitDescription(description: string): { text: string; noSource: boolean } {
  const match = /^\s*источника пока нет[:.]?\s*/i.exec(description)
  if (!match) return { text: description, noSource: false }
  const rest = description.slice(match[0].length)
  return { text: rest.charAt(0).toUpperCase() + rest.slice(1), noSource: true }
}
